import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, MessageSquareOff, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { TranscriptView } from '@/components/conversations/TranscriptView';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorState, type ErrorStateVariant } from '@/components/shared/ErrorState';
import { LeadStatusBadge } from '@/components/shared/LeadStatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { SaveStatus, useSaveStatus } from '@/components/shared/SaveStatus';
import { useAuth } from '@/contexts/AuthContext';
import { useDraft } from '@/hooks/useDraft';
import { ApiError } from '@/lib/api';
import { apiErrorMessage } from '@/lib/apiErrorMessage';
import { formatDate } from '@/lib/formatDate';
import { igHandle } from '@/lib/igHandle';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';
import { conversationService, type ConversationDetail } from '@/services/conversationService';
import { leadService, type Lead, type LeadStatus } from '@/services/leadService';

const TRANSCRIPT_SNIPPET_KEY = 'transcript_snippet';

const STATUS_OPTIONS: readonly LeadStatus[] = [
  'new',
  'engaged',
  'qualified',
  'follow_up',
  'won',
  'lost',
];

// F152 — the conversation card's own async state machine. It is deliberately
// independent of the lead fetch (`error` above): a thread-fetch failure must
// never blank out the rest of the lead page. `empty` covers BOTH a null
// `contact_id` (manual lead, no linked conversation) and a 404 on the thread
// fetch (contact deleted between reads) — both degrade to the same
// explanatory empty state, never a full-page ErrorState.
type ThreadCardState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'error' }
  | { kind: 'loaded'; detail: ConversationDetail };

export function LeadDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const leadId = id ? Number(id) : NaN;

  const [lead, setLead] = useState<Lead | null>(null);
  const [error, setError] = useState<ErrorStateVariant | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [draftRestored, setDraftRestored] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const notesSave = useSaveStatus();

  // F100/A12 — preserve unsaved notes across a session expiry. Keyed by user +
  // lead so a shared browser never shows another account's draft.
  const draft = useDraft<{ notes: string }>(`lead-notes:${user?.id ?? 'anon'}:${leadId}`);

  const fetchLead = useCallback(async () => {
    if (Number.isNaN(leadId)) {
      setError('not-found');
      return;
    }
    setError(null);
    try {
      const result = await leadService.get(leadId);
      setLead(result);
      setNotesDraft(result.notes ?? '');
    } catch (e) {
      // F064 #28 — a missing/failed fetch must render a state, never a
      // forever-blank main. The raw error.message is never shown.
      setError(e instanceof ApiError && e.status === 404 ? 'not-found' : 'load-failed');
    }
  }, [leadId]);

  useEffect(() => {
    void fetchLead();
  }, [fetchLead]);

  // F152 — conversation card: fetch the linked thread independently of the
  // lead itself. `fetchThread` is stable (empty deps) so it's safe to call
  // from both the mount effect and the card's own retry button.
  const [threadState, setThreadState] = useState<ThreadCardState>({ kind: 'loading' });

  const fetchThread = useCallback(async (contactId: number) => {
    setThreadState({ kind: 'loading' });
    try {
      const detail = await conversationService.get(contactId);
      setThreadState({ kind: 'loaded', detail });
    } catch (e) {
      // A 404 (contact deleted between fetches) degrades to the same empty
      // state as a null contact_id — never a full-page ErrorState (FR4).
      setThreadState(
        e instanceof ApiError && e.status === 404 ? { kind: 'empty' } : { kind: 'error' }
      );
    }
  }, []);

  useEffect(() => {
    if (!lead) return;
    if (lead.contact_id == null) {
      setThreadState({ kind: 'empty' });
      return;
    }
    void fetchThread(lead.contact_id);
    // Deliberately keyed on `contact_id`, not the whole `lead` object — a
    // notes save or status change replaces `lead` and must NOT re-trigger a
    // thread refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.contact_id, fetchThread]);

  const handleThreadRetry = () => {
    if (lead && lead.contact_id != null) void fetchThread(lead.contact_id);
  };

  // F100 — restore an unsaved draft once BOTH the lead and the authenticated
  // user are known (the draft key is user-scoped; running before ``user``
  // hydrates would read the wrong key). Guard so a user's own edits are never
  // clobbered by a re-run.
  const restoreAttempted = useRef(false);
  useEffect(() => {
    if (!lead || !user || restoreAttempted.current) return;
    restoreAttempted.current = true;
    const recovered = draft.restore();
    if (recovered && recovered.notes !== (lead.notes ?? '')) {
      setNotesDraft(recovered.notes);
      setDraftRestored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, user]);

  // Persist the in-progress note (debounced) whenever it diverges from what's
  // saved on the lead; clear once they match again.
  useEffect(() => {
    if (!lead) return;
    if (notesDraft !== (lead.notes ?? '')) {
      draft.save({ notes: notesDraft });
    } else {
      draft.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, lead]);

  const persistNotes = () => {
    if (!lead) return;
    const next = notesDraft || null;
    if (next === lead.notes) return;
    // F099/A18 — give the silent blur-save a visible "Salvando… → Salvo ✓".
    void notesSave.save(async () => {
      const updated = await leadService.update(lead.id, { notes: next });
      setLead(updated);
      draft.clear(); // F100 — saved, so no phantom restore later
      setDraftRestored(false);
    });
  };

  const handleStatusChange = async (next: LeadStatus) => {
    if (!lead || lead.status === next) return;
    const updated = await leadService.update(lead.id, { status: next });
    setLead(updated);
  };

  const handleDeleteConfirm = async () => {
    if (!lead) return;
    await leadService.delete(lead.id);
    navigate('/leads');
  };

  if (lead === null && error !== null) {
    return (
      <div className="p-6" data-testid="lead-detail-error">
        <ErrorState
          variant={error}
          cta={
            error === 'not-found' ? (
              <Button asChild variant="outline">
                <Link to="/leads">{t('leads.detail.back_to_list')}</Link>
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => void fetchLead()}>
                  {t('common.retry')}
                </Button>
                <Button asChild variant="ghost">
                  <Link to="/leads">{t('leads.detail.back_to_list')}</Link>
                </Button>
              </>
            )
          }
        />
      </div>
    );
  }

  if (lead === null) {
    return (
      <div className="flex flex-col gap-3 p-6" data-testid="lead-detail-skeleton">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const headline = lead.display_name ?? igHandle(lead.instagram_username) ?? `Lead #${lead.id}`;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={headline}
        subtitle={
          <span className="inline-flex items-center gap-3">
            <LeadStatusBadge status={lead.status} />
            <select
              data-testid="lead-detail-status"
              value={lead.status}
              onChange={(event) => handleStatusChange(event.target.value as LeadStatus)}
              className="min-h-9 rounded-md border bg-background px-2 text-sm"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {t(`leads.status.${status}`)}
                </option>
              ))}
            </select>
          </span>
        }
        actions={
          <Button
            type="button"
            data-testid="lead-detail-delete"
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('leads.detail.delete')}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('leads.detail.info_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[140px_1fr] sm:gap-y-3 [&>dd]:min-w-0 [&>dd]:break-words [&>dt:not(:first-child)]:mt-2 sm:[&>dt:not(:first-child)]:mt-0">
            <dt className="text-muted-foreground">{t('leads.form.instagram_handle')}</dt>
            <dd>{igHandle(lead.instagram_username) ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('leads.form.phone')}</dt>
            <dd>{lead.phone ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('leads.form.email')}</dt>
            <dd>{lead.email ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('leads.detail.source')}</dt>
            <dd>{t(`leads.source.${lead.source}`)}</dd>
            <dt className="text-muted-foreground">{t('leads.detail.first_seen')}</dt>
            <dd>{formatDate(lead.first_seen_at)}</dd>
            <dt className="text-muted-foreground">{t('leads.detail.last_seen')}</dt>
            <dd>{formatDate(lead.last_seen_at)}</dd>
          </dl>
        </CardContent>
      </Card>

      {lead.captured && lead.captured.length > 0 ? (
        <Card data-testid="lead-captured-card">
          <CardHeader>
            <CardTitle>{t('leads.detail.captured_title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[140px_1fr] sm:gap-y-3 [&_dd]:min-w-0 [&_dd]:break-words [&>div:not(:first-child)_dt]:mt-2 sm:[&>div:not(:first-child)_dt]:mt-0">
              {lead.captured
                .filter((item) => item.key !== TRANSCRIPT_SNIPPET_KEY)
                .map((item) => (
                  <div key={item.key} className="contents">
                    <dt className="text-muted-foreground">{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
            </dl>
            {lead.captured
              .filter((item) => item.key === TRANSCRIPT_SNIPPET_KEY)
              .map((item) => (
                <div key={item.key}>
                  <p className="text-xs text-muted-foreground">
                    {t('leads.detail.captured_snippet')}
                  </p>
                  <blockquote className="mt-1 border-l-2 pl-3 text-sm italic text-muted-foreground">
                    {item.value}
                  </blockquote>
                </div>
              ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>{t('leads.detail.notes_title')}</span>
            <SaveStatus
              status={notesSave.status}
              errorMessage={notesSave.error ? apiErrorMessage(notesSave.error, t) : undefined}
              onRetry={() => void notesSave.retry()}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {draftRestored ? (
            <p
              role="status"
              data-testid="lead-notes-draft-restored"
              className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
            >
              {t('common.draft_restored')}
            </p>
          ) : null}
          <Textarea
            data-testid="lead-notes-textarea"
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            onBlur={persistNotes}
            placeholder={t('leads.form.notes')}
            className="min-h-32"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('leads.detail.conversations_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {threadState.kind === 'loading' ? (
            <div className="flex flex-col gap-2" data-testid="lead-conversation-skeleton">
              <Skeleton className="h-12 w-2/3" />
              <Skeleton className="ml-auto h-12 w-1/2" />
              <Skeleton className="h-12 w-3/5" />
            </div>
          ) : threadState.kind === 'error' ? (
            <div
              role="alert"
              className="flex flex-col items-start gap-2"
              data-testid="lead-conversation-error"
            >
              <p className="text-sm text-destructive">{t('leads.detail.conversation_error')}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 w-fit md:min-h-0"
                onClick={handleThreadRetry}
              >
                {t('common.retry')}
              </Button>
            </div>
          ) : threadState.kind === 'empty' ? (
            <EmptyState
              icon={MessageSquareOff}
              title={t('leads.detail.conversation_empty_title')}
              subtitle={t('leads.detail.conversation_empty_body')}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <TranscriptView
                messages={threadState.detail.messages}
                avatarFallbackChar={
                  threadState.detail.summary.ig_username
                    ? threadState.detail.summary.ig_username.charAt(0).toUpperCase()
                    : '#'
                }
                limit={10}
              />
              <Link
                to={`/conversations/${lead.contact_id}`}
                className={cn(
                  'inline-flex items-center gap-1 self-start text-sm font-medium hover:underline',
                  HIT_AREA_44
                )}
                data-testid="lead-conversation-full-link"
              >
                {t('leads.detail.conversation_full_link')}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('leads.detail.delete_confirm_title')}
        description={t('leads.detail.delete_confirm_body')}
        confirmLabel={t('leads.detail.delete')}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
